import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { usePermisos } from '@/hooks/usePermisos';
import { 
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

/**
 * Selector de empresas completo y mejorado
 */
export const EmpresaSelectorComplete = () => {
  const navigate = useNavigate();
  const { 
    empresasDisponibles, 
    seleccionarEmpresa, 
    loadingCambioEmpresa,
    isSuperAdmin,
    usuarioEmpresa 
  } = useSimpleEmpresa();
  const { isAdmin } = usePermisos();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [filteredEmpresas, setFilteredEmpresas] = useState([]);
  const [showCreateOption, setShowCreateOption] = useState(false);

  // Filtrar empresas basado en búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredEmpresas(empresasDisponibles);
      setShowCreateOption(false);
    } else {
      const filtered = empresasDisponibles.filter(empresa =>
        empresa.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        empresa.codigo_empresa.toLowerCase().includes(searchTerm.toLowerCase()) ||
        empresa.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmpresas(filtered);
      setShowCreateOption(filtered.length === 0);
    }
  }, [searchTerm, empresasDisponibles]);

  const handleSelectEmpresa = async (empresaId) => {
    try {
      setSelectedEmpresaId(empresaId);
      await seleccionarEmpresa(empresaId);
      navigate('/dashboard');
    } catch (error) {
      setSelectedEmpresaId(null);
    }
  };

  const getPlanBadgeColor = (planId) => {
    const colors = {
      revvly_free: 'bg-gray-600 text-gray-100',
      revvly_bronze: 'bg-slate-600 text-slate-100',
      revvly_silver: 'bg-amber-600 text-amber-100',
      revvly_gold: 'bg-yellow-600 text-yellow-100'
    };
    return colors[planId] || 'bg-gray-600 text-gray-100';
  };

  const getPlanName = (planId) => {
    const names = {
      revvly_free: 'Free',
      revvly_bronze: 'Iron',
      revvly_silver: 'Bronze',
      revvly_gold: 'Gold'
    };
    return names[planId] || planId;
  };

  const getSubscriptionStatus = (status) => {
    switch (status) {
      case 'active':
        return { text: 'Activa', color: 'text-green-400', icon: CheckCircleIcon };
      case 'trial':
        return { text: 'Prueba', color: 'text-yellow-400', icon: ClockIcon };
      case 'suspended':
        return { text: 'Suspendida', color: 'text-red-400', icon: ExclamationTriangleIcon };
      default:
        return { text: 'Desconocida', color: 'text-gray-400', icon: ExclamationTriangleIcon };
    }
  };

  if (!empresasDisponibles || empresasDisponibles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-gray-800 rounded-xl p-8">
            <BuildingOfficeIcon className="text-gray-400 text-6xl mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-4">No hay empresas disponibles</h2>
            <p className="text-gray-300 mb-6 leading-relaxed">
              No tienes acceso a ninguna empresa. Puedes crear una nueva empresa o solicitar acceso a una existente.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/onboarding/empresa')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Crear Nueva Empresa
              </button>
              <button
                onClick={() => navigate('/solicitar-acceso')}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Solicitar Acceso
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <BuildingOfficeIcon className="h-10 w-10 text-blue-500 mr-3" />
            <span className="text-3xl font-bold text-white">Revvly</span>
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            Selecciona tu Empresa
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {isSuperAdmin 
              ? 'Como Super Administrador, puedes acceder a todas las empresas del sistema'
              : `Tienes acceso a ${empresasDisponibles.length} empresa${empresasDisponibles.length > 1 ? 's' : ''}. Elige la que quieres gestionar.`
            }
          </p>
        </div>

        {/* Barra de búsqueda */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar empresa por nombre o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Estadísticas rápidas */}
        {isSuperAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-400">Total Empresas</p>
                  <p className="text-2xl font-bold text-white">{empresasDisponibles.length}</p>
                </div>
                <BuildingOfficeIcon className="h-8 w-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-400">Empresas Activas</p>
                  <p className="text-2xl font-bold text-white">
                    {empresasDisponibles.filter(e => e.subscription_status === 'active').length}
                  </p>
                </div>
                <CheckCircleIcon className="h-8 w-8 text-green-400" />
              </div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-400">En Prueba</p>
                  <p className="text-2xl font-bold text-white">
                    {empresasDisponibles.filter(e => e.subscription_status === 'trial').length}
                  </p>
                </div>
                <ClockIcon className="h-8 w-8 text-yellow-400" />
              </div>
            </div>
          </div>
        )}

        {/* Grid de empresas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredEmpresas.map((empresa) => {
            const isSelected = selectedEmpresaId === empresa.id;
            const isLoading = isSelected && loadingCambioEmpresa;
            const statusInfo = getSubscriptionStatus(empresa.subscription_status);
            const StatusIcon = statusInfo.icon;

            return (
              <div
                key={empresa.id}
                className={`
                  relative cursor-pointer rounded-xl p-6 border-2 transition-all duration-200
                  ${isSelected
                    ? 'border-blue-500 bg-blue-900/20 scale-105'
                    : 'border-gray-700 bg-gray-800 hover:border-gray-600 hover:bg-gray-750'
                  }
                  ${isLoading ? 'pointer-events-none' : ''}
                `}
                onClick={() => !isLoading && handleSelectEmpresa(empresa.id)}
              >
                {/* Loading overlay */}
                {isLoading && (
                  <div className="absolute inset-0 bg-gray-900/50 rounded-xl flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                )}

                {/* Header con plan y estado */}
                <div className="flex justify-between items-start mb-4">
                  <span className={`
                    px-3 py-1 rounded-full text-xs font-medium
                    ${getPlanBadgeColor(empresa.plan_id)}
                  `}>
                    {getPlanName(empresa.plan_id)}
                  </span>
                  
                  <div className={`flex items-center text-xs ${statusInfo.color}`}>
                    <StatusIcon className="h-4 w-4 mr-1" />
                    {statusInfo.text}
                  </div>
                </div>

                {/* Logo placeholder */}
                <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  {empresa.logo_url ? (
                    <img 
                      src={empresa.logo_url} 
                      alt={empresa.nombre}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
                  )}
                </div>

                {/* Información de la empresa */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white mb-1 line-clamp-2">
                    {empresa.nombre}
                  </h3>
                  
                  <p className="text-sm text-gray-400 mb-2">
                    {empresa.codigo_empresa}
                  </p>

                  {empresa.tagline && (
                    <p className="text-sm text-gray-300 italic mb-3 line-clamp-2">
                      "{empresa.tagline}"
                    </p>
                  )}

                  {/* Información adicional */}
                  <div className="space-y-1 text-xs text-gray-400 mb-4">
                    {empresa.email && (
                      <div className="flex items-center justify-center">
                        <span className="truncate">📧 {empresa.email}</span>
                      </div>
                    )}
                    {empresa.telefono && (
                      <div className="flex items-center justify-center">
                        <span>📞 {empresa.telefono}</span>
                      </div>
                    )}
                    {empresa.ciudad && (
                      <div className="flex items-center justify-center">
                        <span>📍 {empresa.ciudad}</span>
                      </div>
                    )}
                  </div>

                  {/* Información del usuario en esta empresa */}
                  {empresa.usuario_rol && (
                    <div className="bg-gray-700 rounded-lg p-2 mb-4">
                      <div className="flex items-center justify-center text-xs">
                        <UserGroupIcon className="h-4 w-4 mr-1 text-blue-400" />
                        <span className="text-blue-400 font-medium">
                          Tu rol: {empresa.usuario_rol}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Indicador de selección */}
                {isSelected && (
                  <div className="absolute top-4 right-4">
                    <div className="bg-blue-500 rounded-full p-1">
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Opción para crear nueva empresa */}
          {(isSuperAdmin || isAdmin) && (
            <div
              className="relative cursor-pointer rounded-xl p-6 border-2 border-dashed border-gray-600 hover:border-gray-500 bg-gray-800/50 hover:bg-gray-800 transition-all duration-200 flex flex-col items-center justify-center min-h-[280px]"
              onClick={() => navigate('/onboarding/empresa')}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-900/50 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <PlusIcon className="h-8 w-8 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  Crear Nueva Empresa
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                  Configura una nueva empresa desde cero
                </p>
                <div className="inline-flex items-center text-blue-400 text-sm">
                  <SparklesIcon className="h-4 w-4 mr-1" />
                  Comenzar ahora
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Opción de búsqueda sin resultados */}
        {showCreateOption && searchTerm.trim() && (
          <div className="text-center">
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-white font-semibold mb-2">
                No se encontraron empresas con "{searchTerm}"
              </h3>
              <p className="text-gray-400 mb-4">
                ¿Quieres crear una nueva empresa con este nombre?
              </p>
              <button
                onClick={() => navigate('/onboarding/empresa', { 
                  state: { nombreInicial: searchTerm } 
                })}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Crear "{searchTerm}"
              </button>
            </div>
          </div>
        )}

        {/* Opciones adicionales */}
        <div className="text-center mt-12">
          <div className="space-y-4">
            <p className="text-gray-400">
              ¿No encuentras tu empresa?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/solicitar-acceso')}
                className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Solicitar Acceso
              </button>
              <button
                onClick={() => window.location.reload()}
                className="text-gray-400 hover:text-white transition-colors"
              >
                Actualizar Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmpresaSelectorComplete;